import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import {sendAnalyticsEvents} from '../../analytics/common';
import {EVENTS} from '../../analytics/events';
import {selectEventsData, selectOfferType, setEventsData} from '../onboarding/onboardingSlice';
import useLoadScripts from '../../scripts/ScriptProvider';
import stripeSrc from '../../scripts/Stripe';
import DefaultPlans from './DefaultPlans';
import ExperimentalPlans from './ExperimentalPlans';
import {centeredFlex, fadeInAnimation, primaryGreenBtn, screenPadding, tabletWidth} from '../../mixins';
import Slider from './Slider';
import CrossBtn from './CrossBtn';
import CheckoutScreen from './CheckoutScreen';
import { selectStripeAccountName } from '../auth/authSlice';
import { selectPlan } from './paywallSlice';
import { PLANS } from '../../analytics/eCommerce';
import DefaultGzPlans from './GZ_Plans/DefaultGzPlans';
import DiscountGzPlans from './GZ_Plans/DiscountGzPlans';
import TrialGzPlans from './GZ_Plans/TrialGzPlans';
import FreeTrialGzPlans from './GZ_Plans/FreeTrialGzPlans';


const StyledBg = styled.div`
  width: 100vw;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: linear-gradient(180deg, #00BFA5 0%, #00332F 100%);
  opacity: 0.7;
  z-index: 2000;
`;

const StyledWrapper = styled.div`
  width: 100vw;
  height: 100%;
  position: absolute;
  overflow-y: scroll;
  top: 0;
  left: 0;
  z-index: 2100;
  
  @media screen and (min-width: ${tabletWidth}) {
    height: 100vh;
  }
`;

const StyledButton = styled.div`
  ${primaryGreenBtn};
  background-color: #FBA253;
  width: 327px;
  border: none;
  margin-top: 16px;
  
  @media screen and (max-width: 350px) {
    width: 300px;
  }
  @media screen and (max-height: 700px) {
    position: sticky;
    bottom: 24px;
    z-index: 3000;
  }
`;

const StyledContainer = styled.div`
  ${centeredFlex};
  ${screenPadding};
  ${fadeInAnimation};
  
  @media screen and (max-height: 700px) {
    justify-content: flex-start;
  }
  
  @media screen and (min-width: 768px) {
    min-height: unset;
  }
  ${({isGzLandings}) => isGzLandings && `
    background-color: #ffffff;
    position: relative;
  `}
  
  ${({offerType}) => offerType === 'optionsh' && `
    @media screen and (max-height: 670px) {
      overflow-x: scroll;
    }
  `}
`;


const PlansScreen = () => {
    const offerType = useSelector(selectOfferType);
    const [showCheckout, setShowCheckout] = useState(false);
    const amplitudeEventData = useSelector(selectEventsData);
    const plan = useSelector(selectPlan);
    const dispatch = useDispatch();
    const isDefault = ['fullPrice', 'discount', 'optionsc', 'family', 'discountb', 'demo', 'optionsy', 'optionsx', 'up', 'family_fullPrice'].includes(offerType);
    const isExperimentalPlans = ['optionsa', 'optionsb', 'optionsd', 'optionse', 'optionsf', 'intro', 'latam', 'optionsg', 'optionsh', 'optionsj', 'promo', 'trial'].includes(offerType);
    const isDefaultGzPlans = ['gz_fullPrice', 'gz_main', 'gz_getmain', 'gz_anim', 'gz_new'].includes(offerType);
    const isTrialGzPlans = ['gz_trial', 'gz_trial-anim'].includes(offerType);
    const isFreeTrialGzPlans = ['gz_freetrial', 'gz_freetrial-monthly', 'gz_freetrial-weekly', 'gz_month-trial', 'gz_freetrial-anim'].includes(offerType);
    const isDiscountGzPlans = ['gz_discount', 'gz_new-discount','gz_discount-anim'].includes(offerType);
    const isGzLandings = isDefaultGzPlans || isDiscountGzPlans || isTrialGzPlans || isFreeTrialGzPlans;
    const stripeAccountName = useSelector(selectStripeAccountName);
    const handleSetAmplitudeEvents = () => {
        if (!plan) return;
        const { productId, duration, description, periodPrice, price } = PLANS[plan];

        const localEventsData = {
            value: Math.round(periodPrice*100),
            Value: price,
            currency: 'USD',
            price: periodPrice,
            description: description,
            periodPrice: periodPrice,
            product_id: productId,
            duration: duration,
            Duration: duration,
            stripe_account_name: stripeAccountName,
        };
        dispatch(setEventsData(localEventsData));
    };
    const onBuyClick = () => {
        handleSetAmplitudeEvents();
        setShowCheckout(true);
    };

    useLoadScripts({src: stripeSrc, check: 'Stripe'});

    useEffect(() => {
        document.body.style.overflow = 'unset';
        document.body.style.height = '100%';
        sendAnalyticsEvents(EVENTS.plansScreenShown, amplitudeEventData);
        sendAnalyticsEvents(EVENTS.IAPPaywall, amplitudeEventData);
    }, []);

    const plansMappings = [
        { condition: isDefault, component: <>
            <Slider />
            <DefaultPlans />
            <StyledButton onClick={onBuyClick}>Continue</StyledButton>
        </> },
        { condition: isExperimentalPlans, component: <ExperimentalPlans onBuyClick={onBuyClick} /> },
        { condition: isDefaultGzPlans, component: <DefaultGzPlans onBuyClick={onBuyClick} /> },
        { condition: isDiscountGzPlans, component: <DiscountGzPlans onBuyClick={onBuyClick} /> },
        { condition: isTrialGzPlans, component: <TrialGzPlans onBuyClick={onBuyClick} /> },
        { condition: isFreeTrialGzPlans, component: <FreeTrialGzPlans onBuyClick={onBuyClick} /> },
    ];

    return (
        <>
            <StyledBg>
            </StyledBg>
            <StyledWrapper>
                {
                    showCheckout
                        ? <CheckoutScreen onBackClick={() => setShowCheckout(false)}/>
                        : (
                            <StyledContainer isGzLandings={isGzLandings}>
                                <CrossBtn isGzLandings={isGzLandings}/>
                                <>
                                    {plansMappings.map(({ condition, component }) => (
                                        condition && <React.Fragment key={condition}>{component}</React.Fragment>
                                    ))}
                                </>
                            </StyledContainer>
                        )
                }
            </StyledWrapper>
        </>
    );
};


export default PlansScreen;
